<template>
  <v-container>
    <v-card outlined>
      <v-card-title>
        {{ model.verboseNamePlural }}
        <v-dialog v-model="dialog">
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="profile.is_staff"
              depressed
              small
              color="primary"
              class="ml-4"
              v-on="on"
            >
              Aggiungi
              <v-icon right>fa-plus</v-icon>
            </v-btn>
          </template>

          <v-card>
            <v-card-title>
              <template v-if="editedId > -1" class="headline">
                Modifica
              </template>
              <template v-else>Aggiungi</template>
              {{ model.verboseName.toLowerCase() }}
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text class="pb-0 pt-4">
              <span v-if="get(error, 'non_field_errors')" class="red--text">
                {{ error.non_field_errors.join(" ") }}
              </span>
              <slot name="form" v-bind="{ editedItem, error }"></slot>
            </v-card-text>

            <v-divider></v-divider>

            <template v-if="hasAvailabilities">
              <v-card-text class="pt-4">
                <h3 class="font-weight-regular black--text">Disponibilità</h3>
                <availabilities
                  v-if="hasAvailabilityPlace"
                  v-model="editedItem.availabilities"
                  has-place
                ></availabilities>
                <availabilities
                  v-else
                  v-model="editedItem.availabilities"
                ></availabilities>
              </v-card-text>

              <v-divider></v-divider>
            </template>

            <v-card-actions v-if="profile.is_staff">
              <v-spacer></v-spacer>
              <v-btn class="px-4" outlined color="primary" @click="closeDialog">
                Annulla
              </v-btn>
              <v-btn class="px-4" depressed color="primary" @click="save">
                Salva
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-spacer></v-spacer>

        <v-text-field
          v-if="profile.is_staff"
          v-model="search"
          prepend-icon="fa-search"
          label="Cerca"
          class="pt-0 mt-0"
          clearable
          single-line
          hide-details
        ></v-text-field>
      </v-card-title>

      <v-container v-if="profile.is_staff" class="py-0">
        <v-row>
          <v-col
            v-for="filter in filters"
            :key="filter.name"
            :sm="Math.floor(filters.length / 12)"
          >
            <v-checkbox
              v-if="filter.type === 'boolean'"
              v-model="enabledFilters[filter.name]"
              dense
              class="mt-1"
              :label="filter.label"
            ></v-checkbox>
            <autocomplete
              v-else-if="filter.model"
              v-model="enabledFilters[filter.name]"
              lazy
              dense
              clearable
              outlined
              :model="filter.model"
              :extra-params="modelFk"
              :item-text="filter.text"
              item-value="id"
              :label="filter.label || filter.model.verboseName"
            ></autocomplete>
            <v-autocomplete
              v-else-if="filter.values"
              v-model="enabledFilters[filter.name]"
              dense
              clearable
              outlined
              :items="
                Array.isArray(filter.values)
                  ? filter.values
                  : filter.values(unfilteredItems)
              "
              item-text="name"
              item-value="id"
              :label="filter.label"
            ></v-autocomplete>
            <v-menu
              v-else-if="filter.type === 'date'"
              v-model="datePicker[filter.name]"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="enabledFilters[filter.name]"
                  :label="filter.label"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="enabledFilters[filter.name]"
                no-title
                scrollable
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="clearDate(filter.name)">
                  Cancel
                </v-btn>
                <v-btn text color="primary" @click="closeDatePicker(filter.name)">
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </v-container>

      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        :options.sync="options"
        :footer-props="{
          itemsPerPageOptions: [100, 200, 500, 10, 30, 50],
          showCurrentPage: true,
          showFirstLastPage: true,
        }"
        :must-sort="true"
        :loading="loading"
        :elevation="0"
      >
        <template
          v-for="(_, slotName) in nonFormSlots"
          v-slot:[slotName]="context"
        >
          <slot :name="slotName" v-bind="context" />
        </template>

        <template v-slot:item.modified="{ item }">
          <time-ago :date-str="item.modified" />
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon link small class="mr-2" @click="edit(item.id)">
            fa-edit
          </v-icon>
          <v-icon v-if="profile.is_staff" small @click="destroy(item.id)">
            fa-trash
          </v-icon>
        </template>

        <template v-slot:footer>
          <span v-if-superuser>
            <download-csv
              v-if="!customExportUrl"
              delimiter=";"
              :data="items"
              :separator-excel="true"
              :advanced-options="{ newline: '\r\n' }"
              style="position: absolute; font-size: 80%; cursor: pointer;"
              class="py-5 pl-4"
            >
              Esporta elenco
            </download-csv>
            <a
              v-else
              style="position: absolute; font-size: 80%; cursor: pointer;"
              class="py-5 pl-4"
              @click="exportCsv()"
            >
              Esporta elenco
            </a>
          </span>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { mapState } from "vuex"
import { cloneDeep, debounce, pickBy } from "@/lodash"
import download from "downloadjs"

const defaultOptions = () => ({
  page: 1,
  itemsPerPage: 50,
  // sortBy: ["id"],
  // sortDesc: [true],
})

export default {
  props: {
    modelHeaders: {
      type: Array,
      default: () => [],
    },
    model: {
      type: Object,
      required: true,
    },
    modelFk: {
      type: Object,
      required: true,
    },
    defaultItem: {
      type: Function,
      required: true,
    },
    filters: {
      type: Array,
      default: () => [],
    },
    hasAvailabilities: {
      type: Boolean,
      default: false,
    },
    hasAvailabilityPlace: {
      type: Boolean,
      default: false,
    },
    customExportUrl: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      error: {},
      options: defaultOptions(),
      search: "",
      dialog: false,
      editedItem: this.defaultItem(),
      editedId: -1,
      enabledFilters: this.filters.reduce((acc, f) => {
        acc[f.name] = undefined
        return acc
      }, {}),
      datePicker: {},
    }
  },
  computed: {
    headers() {
      return this.modelHeaders.concat([
        { text: "Modificato", value: "modified" },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "end",
        },
      ])
    },
    activeFilters() {
      return pickBy(this.enabledFilters)
    },
    nonFormSlots() {
      // eslint-disable-next-line no-unused-vars
      const { form, ...slots } = this.$scopedSlots
      return slots
    },
    items() {
      let items = this.unfilteredItems
      for (const [filterName, filterValue] of Object.entries(
        this.activeFilters
      )) {
        items = items.filter((item) => {
          if (filterName === 'date') {
            return item[filterName].substring(0, 10) === filterValue
          } else if (item[filterName] && item[filterName].constructor === Array) {
            return item[filterName].includes(filterValue)
          } else {
            return item[filterName] === filterValue
          }
        })
      }
      return items
    },
    ...mapState({
      loading(state) {
        return state[this.model.appName].pending[
          `all${this.model.modelNamePlural}`
        ]
      },
      unfilteredItems(state) {
        return state[this.model.appName][`all${this.model.modelNamePlural}`]
      },
    }),
  },
  mounted() {
    this.fetch()
  },
  methods: {
    fetch() {
      this.$store.dispatch(`listAll${this.model.modelNamePlural}`, {
        params: {
          ...this.modelFk,
        },
      })
    },
    edit(itemId) {
      this.editedId = itemId
      this.editedItem = cloneDeep(this.items.find((item) => item.id === itemId))
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.error = {}
        this.editedItem = Object.assign({}, this.defaultItem())
        this.editedId = -1
      })
    },
    async exportCsv() {
      let filters = this.modelFk
      // query the ids of currently filtered items
      if (Object.keys(this.activeFilters).length) {
        filters.id__in = this.items.map((i) => i.id).join(",")
      }
      const params = Object.entries(filters)
        .map(([key, val]) => `${key}=${val}`)
        .join("&")

      this.axios.get(`${this.customExportUrl}?${params}`).then((resp) => {
        const data = resp.data.data
        download(data, "export.csv", "text/csv")
      })
    },
    async save() {
      let request
      if (this.editedId > -1) {
        // edit request
        request = this.$store.dispatch(`update${this.model.modelName}`, {
          params: { id: this.editedId },
          data: this.editedItem,
        })
      } else {
        // create request
        request = this.$store.dispatch(`create${this.model.modelName}`, {
          data: Object.assign({}, this.editedItem, this.modelFk),
        })
      }
      try {
        await request
        this.$store.commit("core/setSnack", {
          level: "success",
          msg: "Salvato",
        })
        this.fetch()
        this.closeDialog()
      } catch (error) {
        this.error = error.response.data
      }
    },
    async destroy(id) {
      let confirm = window.confirm(
        "Si è sicuri di voler cancellare questo elemento? L'azione è irreversibile."
      )
      if (confirm) {
        try {
          await this.$store.dispatch(`destroy${this.model.modelName}`, {
            params: { id },
          })
          this.$store.commit("core/setSnack", {
            level: "success",
            msg: "Eliminato",
          })
          this.fetch()
        } catch (error) {
          window.alert(error.response.data.error)
        }
      }
    },
    delayedUpdate: debounce(function () {
      this.options.page = 1
      this.fetch()
    }, 500),
    clearDate(filterName) {
      this.enabledFilters[filterName] = undefined
      this.datePicker[filterName] = false
    },
    closeDatePicker(filterName) {
      this.datePicker[filterName] = false
    },
  },
}
</script>
